import { Injectable } from "@angular/core";
import { AbstractControl, ValidatorFn } from "@angular/forms";
import { LoadingService } from "./loading.service";
import { Router } from "@angular/router";
import { AlertService } from "./alert.service";
import { ProjectConfigService } from "./project-config.service";
import { NotificationService } from "./notification.service";

@Injectable({
	providedIn: 'root'
})
export class XtrasService {

    constructor(
        private alert: AlertService,
        private load: LoadingService,
        private toast: NotificationService,
        private router: Router,
        private projectConfig: ProjectConfigService,
    ) {}

    filterEmptyParams(formData: { [key: string]: any }) {
        return Object.fromEntries(
            Object.entries(formData).filter(
                ([_, value]) => value !== null && value !== undefined && value !== ''
            )
        );
    }

    lengthItems(items: any) {
        if(Array.isArray(items)) {
            if(items.length >= 1) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    scrollToTarget(el: HTMLElement) { // example <div #algo> scrollToTarget(algo)
        el.scrollIntoView({behavior: 'smooth'});
    }

    checkFieldsSame(field1: string, field2: string): ValidatorFn {
        return (group: AbstractControl): { [key: string]: any } | null => {
            const _field1 = group.get(field1);
            const _field2 = group.get(field2);
            return _field1 && _field2 && _field1.value === _field2.value ? null : { notSame: true };
        };
    }

    getErrorApi(result: any, loading:boolean = false, redirectError: string = '/', errorDefault?:string) {
        if(loading) {
            this.load.dismiss();
        }
        if(result?.others?.redirect_url) {
            this.router.navigateByUrl(result?.others?.redirect_url);
            this.alertErrorApi(result, errorDefault);
            return;
        }
        if(result['redirect']) {
          this.router.navigateByUrl(redirectError);
        }
        this.alertErrorApi(result, errorDefault);
    }

    alertErrorApi(result: any, errorDefault?: string) {
        if(result['message']) {
            if(result['errors']) {
                this.alert.alertCapYei({
                    type: 'error',
                    message: result['message'],
                    buttons: {
                        cancel: { show: false },
                        confirm: { show: true, text: 'Entendido' },
                    },
                    description: result['errors']
                });
            } else {
                this.alert.alertCapYei({
                    type: 'error',
                    message: 'Error',
                    buttons: {
                        cancel: { show: false },
                        confirm: { show: true, text: 'Entendido' },
                    },
                    description: result['message']
                });
            }
        } else {
            if(result['errors']) {
                this.alert.alertCapYei({
                    type: 'error',
                    message: '¡Error!',
                    buttons: {
                        cancel: { show: false },
                        confirm: { show: true, text: 'Entendido' },
                    },
                    description: result['errors']
                });
            } else {
                this.toast.error((errorDefault ?? 'No se pudo obtener la información, intente nuevamente.'), '¡Error!',  {timeOut: 5000, closeButton: true, progressBar: true});
            }
        }
    }

    determineColorType(color: string): 'light' | 'dark' | 'normal' | 'unknown' {
        try {
            // Convertimos el color a formato RGBA
            const rgba = this.parseColorToRGBA(color);
            if (!rgba) return 'unknown';
    
            // Calculamos el brillo usando la fórmula de luminosidad perceptiva
            const [r, g, b] = rgba;
            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
            // Definimos umbrales más estrictos
            const LIGHT_THRESHOLD = 200;  // Más cerca del blanco (255)
            const DARK_THRESHOLD = 60;    // Más cerca del negro (0)
    
            if (brightness >= LIGHT_THRESHOLD) {
                return 'light';
            } else if (brightness <= DARK_THRESHOLD) {
                return 'dark';
            } else {
                return 'normal';  // Colores en el rango medio
            }
        } catch {
            return 'unknown';
        }
    }

    // Convierte cualquier formato de color a RGBA
    parseColorToRGBA(color: string): [number, number, number, number] | null {
        // Eliminar espacios y convertir a minúsculas
        const normalized = color.trim().toLowerCase();
    
        // 1. Hex color (#rgb, #rgba, #rrggbb, #rrggbbaa)
        if (/^#([a-f0-9]{3,4}|[a-f0-9]{6}|[a-f0-9]{8})$/.test(normalized)) {
            let hex = normalized.substring(1);
            
            // Expandir formato corto (#rgb → #rrggbb)
            if (hex.length === 3 || hex.length === 4) {
                hex = hex.split('').map(c => c + c).join('');
            }
            
            // Parsear componentes
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            const a = hex.length === 8 ? parseInt(hex.substring(6, 8), 16) / 255 : 1;
            
            return [r, g, b, a];
        }
        
        // 2. RGB/RGBA color (rgb(255, 255, 255) o rgba(255, 255, 255, 0.5))
        const rgbMatch = normalized.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
        if (rgbMatch) {
            const r = parseInt(rgbMatch[1]);
            const g = parseInt(rgbMatch[2]);
            const b = parseInt(rgbMatch[3]);
            const a = rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1;
            return [r, g, b, a];
        }
        
        // 3. Nombres de colores básicos (opcional)
        const colorNames: Record<string, [number, number, number, number]> = {
            'white': [255, 255, 255, 1],
            'black': [0, 0, 0, 1],
            'red': [255, 0, 0, 1],
            'green': [0, 128, 0, 1],
            'blue': [0, 0, 255, 1],
            // Añadir más colores según necesidad
        };
        
        if (colorNames[normalized]) {
            return colorNames[normalized];
        }
        
        return null;
    }

    loadInlineScriptBody(text: string) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.text = text;
        document.body.appendChild(script);
    }
    
    loadScriptBody(src: string, async: boolean = false) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;
        script.async = async;
        document.body.appendChild(script);
    }

    loadScriptHead(src: string, async: boolean = false) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;
        script.async = async;
        document.head.appendChild(script);
    }

    staticMap(lat: string|number, lng: string|number) {
        return `https://maps.googleapis.com/maps/api/staticmap?key=${this.projectConfig.getConfig().keyMap}&size=500x500&zoom=17&maptype=roadmap&markers=color:red|${lat},${lng}`;
    }

    getIDDateTime() {
		var today = new Date();
		var y = today.getFullYear();
		// JavaScript months are 0-based.
		var m = today.getMonth() + 1;
		var d = today.getDate();
		var h = today.getHours();
		var mi = today.getMinutes();
		var s = today.getSeconds();
		return y + '_' + m + '_' + d + '_' + h + '_' + mi + '_' + s;
	}
    
    async getFormData(data: { [key: string]: any }): Promise<FormData> {
        let formData = new FormData();

        await Promise.all(Object.entries(data).map( ([key, val]:any) => {
            if(Array.isArray(val)) {
                val.forEach((item: any) => {
                    // formData.append(`${key}[]`, item);
                    formData.append(key, item);
                });
            } else if (val !== null && val !== undefined && val !== '') {
                formData.append(key, val);
            }
        }));
      
        return formData;
    }

    getAction(action?: string): string {
        switch (action) {
            case 'edit':
                return 'Editar';
        
            case 'read':
                return 'Ver';

            case 'delete':
                return 'Eliminar';
            
            default:
                return 'Crear';
        }
    }

    emptyDataApi(result: any, loading:boolean = false, redirectError: string = '/', errorDefault?:string) {
        if(loading) {
            this.load.dismiss();
        }
        this.toast.success('¡Éxito!', 'Resultados filtrados exitósamente.', 4000);
    }

    getTextWithChangedParameters(text: string,  params: { [key: string]: string | number }): string {
        return text.replace(/{(\w+)}/g, (match, key) => {
            return key in params ? params[key].toString() : match;
        });
    }
}